import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface PePageHeaderProps {
  title: ReactNode;
  /**
   * Destination for the back control. Defaults to the PE dashboard.
   * Pass `false` to hide the back button (e.g. dashboard itself).
   */
  backTo?: string | number | false;
  backLabel?: string;
}

/**
 * Compact page chrome: back control + title on one row so the search
 * bar can sit immediately underneath at the top of the viewport.
 */
export default function PePageHeader({
  title,
  backTo = '/project-essential',
  backLabel = 'Back to Dashboard',
}: PePageHeaderProps) {
  const navigate = useNavigate();
  const showBack = backTo !== false;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ minWidth: 0, gap: { xs: 0.75, sm: 1.25 } }}
    >
      {showBack ? (
        <Button
          onClick={() => {
            if (typeof backTo === 'number') navigate(backTo);
            else navigate(backTo || '/project-essential');
          }}
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          size="small"
          sx={{
            flexShrink: 0,
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 999,
            borderColor: 'var(--color-border-strong)',
            color: 'var(--color-text-strong)',
            bgcolor: 'var(--color-surface)',
            px: { xs: 1, sm: 1.5 },
            '& .MuiButton-startIcon': { mr: { xs: 0.25, sm: 0.75 } },
            '&:hover': {
              borderColor: 'var(--color-accent)',
              bgcolor: 'var(--color-accent-soft)',
            },
          }}
        >
          <Box
            component="span"
            sx={{
              display: { xs: 'none', sm: 'inline' },
            }}
          >
            {backLabel}
          </Box>
          <Box
            component="span"
            sx={{
              display: { xs: 'inline', sm: 'none' },
            }}
          >
            {backLabel === 'Back to Dashboard' ? 'Back' : backLabel}
          </Box>
        </Button>
      ) : null}

      <Typography
        variant="h4"
        sx={{
          flex: 1,
          minWidth: 0,
          fontWeight: 800,
          color: 'var(--color-accent)',
          textAlign: showBack ? { xs: 'left', sm: 'center' } : 'center',
          letterSpacing: '0.01em',
          fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2.25rem' },
          lineHeight: 1.2,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          // When back is present, optically center the title on sm+
          // by mirroring the back button width with a spacer.
          ...(showBack
            ? {
                pr: { sm: 0 },
              }
            : {}),
        }}
      >
        {title}
      </Typography>
    </Stack>
  );
}
